import React, { PropsWithChildren, useId } from 'react';
import { Box, List, Text } from '@mantine/core';
import { BaseFieldProps } from './types';
import { FieldWithValue } from 'src/UI/dynamicUI/types';
import { FieldTypeMetadata } from 'src/data-capture/types';
import { Logger } from 'src/utils/Logger';
import { formatCurrency } from 'src/common/currency';

type ViewFieldTextProps = {
  labelId: string;
};
const ViewFieldText = ({ labelId, children }: PropsWithChildren<ViewFieldTextProps>) => {
  return (
    <Text
      size="sm"
      fw={600}
      c="gray.9"
      aria-labelledby={labelId}
      style={{ whiteSpace: 'pre-line' }}
    >
      {children}
    </Text>
  );
};

export const ViewFormField = (props: BaseFieldProps) => {
  const { fieldLabel, readModePrepend, readModeAppend } = props;
  const formattedValue = getFormattedValue(props);
  return (
    <ViewField
      fieldLabel={fieldLabel}
      value={formattedValue}
      readModeAppend={readModeAppend}
      readModePrepend={readModePrepend}
    />
  );
};

type ViewFieldProps = {
  fieldLabel?: string;
  value: string | string[] | undefined;
  readModePrepend?: string;
  readModeAppend?: string;
};
export const ViewField = ({
  fieldLabel,
  value,
  readModeAppend,
  readModePrepend,
}: ViewFieldProps) => {
  const labelId = useId();
  return (
    <Box>
      <Text size="sm" fw={500} mb={4} c="gray.7" id={labelId}>
        {fieldLabel}
      </Text>

      {Array.isArray(value) ? (
        <List spacing={'md'}>
          {value.map((item, index) => (
            <List.Item key={index}>
              <ViewFieldText labelId={labelId}>{item}</ViewFieldText>
            </List.Item>
          ))}
        </List>
      ) : (
        <ViewFieldText labelId={labelId}>
          {readModePrepend}
          {value || <br />}
          {readModeAppend}
        </ViewFieldText>
      )}
    </Box>
  );
};

const getFormattedValue = (props: BaseFieldProps): string | string[] | undefined => {
  const { form, formFieldName, entityMetadata } = props;
  const value = getFieldValue(form.getValues(), formFieldName);

  return formatValueForField(value, entityMetadata);
};

export function formatValueForField(value: unknown, entityMetadata: FieldTypeMetadata | undefined) {
  if (value == null) {
    return;
  }

  if (entityMetadata == null) {
    return Array.isArray(value) ? value : `${value}`;
  }

  const fieldType = entityMetadata.type;

  if (fieldType === 'SELECT' || fieldType === 'BOOLEAN') {
    const option = entityMetadata.options.find(opt => opt.value.toString() === value?.toString());
    return option?.label;
  }

  if (fieldType === 'MONEY') {
    return formatCurrency(value, entityMetadata.currency);
  }

  if (fieldType === 'DATE') {
    if (value instanceof Date) {
      return value.toLocaleDateString('en-GB');
    }
    if (typeof value !== 'string') {
      Logger.warn('Value is not a string or date for DATE', { value });
      return;
    }
    return new Date(value).toLocaleDateString('en-GB');
  }

  if (fieldType === 'PERCENTAGE') {
    return `${value}%`;
  }

  if (fieldType === 'LIST') {
    if (!Array.isArray(value)) {
      Logger.warn('Value is not an array for LIST', { value });
      return;
    }

    return value;
  }

  if (fieldType === 'TEXT' || fieldType === 'NUMBER' || fieldType === 'STRING') {
    return `${value}`;
  }

  fieldType satisfies never;
  return;
}

function getFieldValue(
  values: Record<string, FieldWithValue | FieldWithValue[]>,
  path: string,
): unknown {
  const parts = path.split('.');
  const getPart = (part: string) => {
    if (!isNaN(Number(part)) && Array.isArray(values)) {
      return values?.[Number(part)];
    }

    if (values != null && typeof values === 'object' && part in values) {
      return values[part];
    }

    return undefined;
  };

  if (parts.length === 1) {
    return getPart(parts[0]);
  }

  const [first, ...rest] = parts;
  const next = getPart(first);

  return next ? getFieldValue(next, rest.join('.')) : undefined;
}
